import React, { useState, useEffect } from 'react';
import { logsApi, LogEntry } from '../../api/logsApi';
import { format } from 'date-fns';
import { ru } from 'date-fns/locale';

const LogsPage: React.FC = () => {
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(50);
  const [filters, setFilters] = useState({
    fromDate: '',
    toDate: '',
    level: '',
    logger: ''
  });
  const [showAutopiterOnly, setShowAutopiterOnly] = useState(false);

  const loadLogs = async () => {
    setLoading(true);
    try {
      const params = {
        ...filters,
        page: currentPage,
        pageSize
      };

      const response = showAutopiterOnly 
        ? await logsApi.getAutopiterLogs(params)
        : await logsApi.getLogs(params);

      setLogs(response.data);
      setTotalCount(response.totalCount);
    } catch (error) {
      console.error('Ошибка при загрузке логов:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadLogs();
  }, [currentPage, filters, showAutopiterOnly]);

  const handleFilterChange = (key: string, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setCurrentPage(1);
  };

  const handleClearFilters = () => {
    setFilters({
      fromDate: '',
      toDate: '',
      level: '',
      logger: ''
    });
    setCurrentPage(1);
  };

  const handleClearOldLogs = async () => {
    if (window.confirm('Удалить логи старше 30 дней?')) {
      try {
        await logsApi.clearOldLogs(30);
        alert('Старые логи удалены');
        loadLogs();
      } catch (error) {
        console.error('Ошибка при удалении логов:', error);
        alert('Ошибка при удалении логов');
      }
    }
  };

  const getLevelColor = (level: string) => {
    switch (level.toLowerCase()) {
      case 'error': return 'text-red-600 bg-red-50';
      case 'warning': return 'text-yellow-600 bg-yellow-50';
      case 'information': return 'text-blue-600 bg-blue-50';
      case 'debug': return 'text-gray-600 bg-gray-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  const totalPages = Math.ceil(totalCount / pageSize);

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Логи системы</h1>
        <div className="flex gap-2">
          <button
            onClick={handleClearOldLogs}
            className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
          >
            Очистить старые логи
          </button>
        </div>
      </div>

      {/* Фильтры */}
      <div className="bg-white p-4 rounded-lg shadow mb-6">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">С даты</label>
            <input
              type="date"
              value={filters.fromDate}
              onChange={(e) => handleFilterChange('fromDate', e.target.value)}
              className="w-full px-3 py-2 border rounded"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">По дату</label>
            <input
              type="date"
              value={filters.toDate}
              onChange={(e) => handleFilterChange('toDate', e.target.value)}
              className="w-full px-3 py-2 border rounded"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Уровень</label>
            <select
              value={filters.level}
              onChange={(e) => handleFilterChange('level', e.target.value)}
              className="w-full px-3 py-2 border rounded"
            >
              <option value="">Все</option>
              <option value="Error">Error</option>
              <option value="Warning">Warning</option>
              <option value="Information">Information</option>
              <option value="Debug">Debug</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Логгер</label>
            <input
              type="text"
              value={filters.logger}
              onChange={(e) => handleFilterChange('logger', e.target.value)}
              placeholder="Например: AutopiterProvider"
              className="w-full px-3 py-2 border rounded"
            />
          </div>
          <div className="flex items-end">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={showAutopiterOnly}
                onChange={(e) => setShowAutopiterOnly(e.target.checked)}
                className="mr-2"
              />
              Только Autopiter
            </label>
          </div>
        </div>
        <div className="mt-4">
          <button
            onClick={handleClearFilters}
            className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
          >
            Сбросить фильтры
          </button>
        </div>
      </div>

      {/* Статистика */}
      <div className="bg-white p-4 rounded-lg shadow mb-6">
        <div className="flex justify-between items-center">
          <div>
            <span className="font-medium">Всего записей: </span>
            <span className="text-blue-600">{totalCount}</span>
          </div>
          <div>
            <span className="font-medium">Страница: </span>
            <span className="text-blue-600">{currentPage} из {totalPages}</span>
          </div>
        </div>
      </div>

      {/* Таблица логов */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-2">Загрузка логов...</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Время
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Уровень
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Логгер
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Сообщение
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {logs.map((log) => (
                  <tr key={log.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {format(new Date(log.timestamp), 'dd.MM.yyyy HH:mm:ss', { locale: ru })}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getLevelColor(log.level)}`}>
                        {log.level}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {log.logger}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      <div>
                        <div className="font-medium">{log.message}</div>
                        {log.exception && (
                          <details className="mt-2">
                            <summary className="cursor-pointer text-red-600 text-xs">
                              Показать исключение
                            </summary>
                            <pre className="mt-1 text-xs bg-red-50 p-2 rounded overflow-x-auto">
                              {log.exception}
                            </pre>
                          </details>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Пагинация */}
      {totalPages > 1 && (
        <div className="mt-6 flex justify-center">
          <nav className="flex items-center space-x-2">
            <button
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
              className="px-3 py-2 border rounded disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Назад
            </button>
            
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              const page = Math.max(1, Math.min(totalPages - 4, currentPage - 2)) + i;
              return (
                <button
                  key={page}
                  onClick={() => setCurrentPage(page)}
                  className={`px-3 py-2 border rounded ${
                    currentPage === page ? 'bg-blue-600 text-white' : 'hover:bg-gray-50'
                  }`}
                >
                  {page}
                </button>
              );
            })}
            
            <button
              onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
              disabled={currentPage === totalPages}
              className="px-3 py-2 border rounded disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Вперед
            </button>
          </nav>
        </div>
      )}
    </div>
  );
};

export default LogsPage;
